/**
 * ProjectProgressPanel Component
 * Unified view of project progress and next steps
 * Merges StatusProgressIndicator and NextStepsPanel functionality
 */

import React, { useState, useEffect } from 'react';
import StatusBadge from '../common/StatusBadge';
import { WORKFLOW_STATUS } from '../../utils/constants';
import { formatDeadline } from '../../utils/formatters';

const ProjectProgressPanel = ({ project, userRole, projectHistory = [] }) => {
  const [showFullProgress, setShowFullProgress] = useState(false);
  const [showAllAmendments, setShowAllAmendments] = useState(false);
  const [isMobileCollapsed, setIsMobileCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Define the workflow phases
  const workflowPhases = [
    { name: 'Sales', statuses: [WORKFLOW_STATUS.PENDING_PROJECT_NUMBER, WORKFLOW_STATUS.ACTIVE_PROJECT] },
    { name: 'Measurement', statuses: [WORKFLOW_STATUS.MEASUREMENT_PENDING, WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS, WORKFLOW_STATUS.MEASUREMENT_DONE] },
    { name: '2D Design', statuses: [WORKFLOW_STATUS.TWO_D_PENDING, WORKFLOW_STATUS.TWO_D_IN_PROGRESS, WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW, WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED, WORKFLOW_STATUS.TWO_D_APPROVED] },
    { name: '3D Design', statuses: [WORKFLOW_STATUS.THREE_D_PENDING, WORKFLOW_STATUS.THREE_D_IN_PROGRESS, WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW, WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED, WORKFLOW_STATUS.THREE_D_APPROVED] },
    { name: 'DD & Quotation', statuses: [WORKFLOW_STATUS.DD_PENDING, WORKFLOW_STATUS.DD_IN_PROGRESS, WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION, WORKFLOW_STATUS.QUOTATION_IN_PROGRESS] },
    { name: 'Contract', statuses: [WORKFLOW_STATUS.READY_FOR_SIGNING, WORKFLOW_STATUS.SIGNING_SCHEDULED, WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED] },
    { name: 'Closed', statuses: [WORKFLOW_STATUS.PROJECT_CLOSED_WON, WORKFLOW_STATUS.PROJECT_CLOSED_LOST, WORKFLOW_STATUS.PROJECT_CANCELLED] }
  ];

  // Find current phase index
  const currentPhaseIndex = workflowPhases.findIndex(phase =>
    phase.statuses.includes(project.workflowStatus)
  );

  // Calculate progress percentage
  const progressPercent = Math.round(((currentPhaseIndex + 1) / workflowPhases.length) * 100);

  // Get current phase name
  const currentPhaseName = currentPhaseIndex >= 0 ? workflowPhases[currentPhaseIndex].name : 'Unknown';

  // Define next steps for each status
  // Roles: director, manager, leader, agent, telemarketer, sid, asid, designer, draughter_2d, draughter_site, quotation_team, legal_team
  const getNextSteps = () => {
    const stepsMap = {
      [WORKFLOW_STATUS.PENDING_PROJECT_NUMBER]: [
        { text: 'Assign project number in system', role: ['manager'], priority: 'high' },
        { text: 'Verify sales form details', role: ['manager', 'leader'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.ACTIVE_PROJECT]: [
        { text: 'Schedule measurement appointment', role: ['agent', 'manager'], priority: 'high' },
        { text: 'Prepare measurement tools', role: ['agent'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.MEASUREMENT_PENDING]: [
        { text: 'Confirm appointment with client', role: ['agent'], priority: 'high' },
        { text: 'Self-assign or wait for assignment', role: ['draughter_site'], priority: 'medium' },
        { text: 'Review project scope', role: ['agent', 'draughter_site'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS]: [
        { text: 'Complete on-site measurements', role: ['draughter_site'], priority: 'high' },
        { text: 'Take measurement photos', role: ['draughter_site'], priority: 'high' },
        { text: 'Upload measurement files', role: ['draughter_site'], priority: 'high' },
        { text: 'Submit measurement with notes', role: ['draughter_site'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.MEASUREMENT_DONE]: [
        { text: 'Assign to SID for 2D drawing', role: ['manager'], priority: 'high' },
        { text: 'Review measurement completeness', role: ['sid', 'manager'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.TWO_D_PENDING]: [
        { text: 'Start 2D drawing work', role: ['sid', 'draughter_2d'], priority: 'high' },
        { text: 'Review measurement files', role: ['sid', 'draughter_2d'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.TWO_D_IN_PROGRESS]: [
        { text: 'Complete 2D drawings', role: ['sid', 'draughter_2d'], priority: 'high' },
        { text: 'Upload 2D drawing files', role: ['sid', 'draughter_2d'], priority: 'high' },
        { text: 'Submit for client review', role: ['sid', 'draughter_2d'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW]: [
        { text: 'Send 2D drawings to client', role: ['agent', 'manager'], priority: 'high' },
        { text: 'Approve or request amendments', role: ['manager', 'leader'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED]: [
        { text: 'Review amendment notes', role: ['sid', 'draughter_2d'], priority: 'high' },
        { text: 'Make requested changes', role: ['sid', 'draughter_2d'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.TWO_D_APPROVED]: [
        { text: 'Assign to ASID for 3D design', role: ['manager'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.THREE_D_PENDING]: [
        { text: 'Start 3D design work', role: ['asid'], priority: 'high' },
        { text: 'Review approved 2D drawings', role: ['asid'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.THREE_D_IN_PROGRESS]: [
        { text: 'Complete 3D renders', role: ['asid'], priority: 'high' },
        { text: 'Upload 3D render files', role: ['asid'], priority: 'high' },
        { text: 'Submit for client review', role: ['asid'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW]: [
        { text: 'Send 3D renders to client', role: ['agent', 'manager'], priority: 'high' },
        { text: 'Approve or request amendments', role: ['manager', 'leader'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED]: [
        { text: 'Review amendment notes', role: ['asid'], priority: 'high' },
        { text: 'Make requested changes', role: ['asid'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.THREE_D_APPROVED]: [
        { text: 'Start DD Phase', role: ['manager', 'sid', 'asid'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.DD_PENDING]: [
        { text: 'Start DD work', role: ['sid', 'asid'], priority: 'high' },
        { text: 'Review 3D approved files', role: ['sid', 'asid'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.DD_IN_PROGRESS]: [
        { text: 'Complete detailed drawings (DD)', role: ['sid', 'asid'], priority: 'high' },
        { text: 'Upload BQ (without cost)', role: ['sid', 'asid'], priority: 'high' },
        { text: 'Submit to Quotation Team', role: ['sid', 'asid'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION]: [
        { text: 'Review DD files', role: ['quotation_team'], priority: 'high' },
        { text: 'Start quotation preparation', role: ['quotation_team'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.QUOTATION_IN_PROGRESS]: [
        { text: 'Complete quotation items', role: ['quotation_team'], priority: 'high' },
        { text: 'Finalize quotation', role: ['quotation_team', 'manager'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.READY_FOR_SIGNING]: [
        { text: 'Generate contract document', role: ['manager', 'legal_team'], priority: 'high' },
        { text: 'Schedule signing appointment', role: ['manager', 'agent'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.SIGNING_SCHEDULED]: [
        { text: 'Prepare contract documents', role: ['manager', 'agent', 'legal_team'], priority: 'high' },
        { text: 'Send appointment reminder', role: ['agent'], priority: 'medium' },
        { text: 'Record signing outcome', role: ['manager', 'agent'], priority: 'high' }
      ],
      [WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED]: [
        { text: 'Review client negotiation feedback', role: ['sid', 'asid', 'manager'], priority: 'high' },
        { text: 'Revise DD or quotation as needed', role: ['sid', 'asid'], priority: 'high' },
        { text: 'Follow up with client', role: ['agent', 'manager'], priority: 'high' },
        { text: 'Mark as Ready for Signing when resolved', role: ['sid', 'asid', 'manager'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.PROJECT_CLOSED_WON]: [
        { text: 'Project won! Proceed to installation', role: ['manager'], priority: 'info' }
      ],
      [WORKFLOW_STATUS.PROJECT_CLOSED_LOST]: [
        { text: 'Review loss reason for insights', role: ['manager', 'leader'], priority: 'medium' }
      ],
      [WORKFLOW_STATUS.PROJECT_CANCELLED]: [
        { text: 'Archive project documentation', role: ['manager'], priority: 'low' }
      ]
    };

    return stepsMap[project.workflowStatus] || [];
  };

  const nextSteps = getNextSteps();

  // Check if project is in amendment status
  const isInAmendmentStatus = [
    WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED,
    WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED,
    WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED
  ].includes(project.workflowStatus);

  // Extract and combine all workflow notes (measurement submissions, 2D/3D submissions, DD submissions, amendments, approvals, quotation, signing, outcomes) into one array
  const workflowNotes = projectHistory
    .filter(item => {
      // Measurement completion/submission events (direct changeType from service)
      if (item.changeType === 'measurement_completed') return true;
      // 2D/3D Submission events (direct changeType from service)
      if (item.changeType === '2d_submitted' || item.changeType === '3d_submitted') return true;
      // DD Submission events (direct changeType from service)
      if (item.changeType === 'dd_submitted') return true;
      // Contract signed event
      if (item.changeType === 'contract_signed') return true;
      // Project closed event (lost/cancelled)
      if (item.changeType === 'project_closed') return true;
      // Quotation finalized event
      if (item.changeType === 'quotation_finalized') return true;
      // Signing scheduled event (new changeType)
      if (item.changeType === 'signing_scheduled') return true;
      // Negotiation blocker event (new changeType)
      if (item.changeType === 'negotiation_blocker') return true;
      // Legacy: Signing scheduled event (logged as 'other' with appointmentId in metadata)
      if (item.changeType === 'other' && item.metadata?.appointmentId) {
        // Check if it's a signing scheduled or blocker event
        if (item.metadata?.scheduledDate || item.metadata?.blockerType) return true;
      }
      // Status changes - covers workflow transitions from UI (generic status updates)
      if (item.changeType === 'status_changed') {
        const newValue = (item.newValue || '').toLowerCase();
        return (
          // Measurement completion (when UI uses generic status update)
          newValue === 'measurement_done' ||
          // 2D/3D Submissions (when UI uses generic status update)
          newValue === '2d_pending_client_review' || newValue === '3d_pending_client_review' ||
          // DD Submission (when UI uses generic status update)
          newValue === 'dd_submitted_to_quotation' ||
          // Amendments
          newValue === '2d_amendment_required' || newValue === '3d_amendment_required' ||
          newValue === 'signing_amendment_required' ||
          // Note: Approvals (2d_approved, 3d_approved), quotation finalized (ready_for_signing),
          // and signing_scheduled are excluded here because they have dedicated changeTypes to avoid duplicate entries
          // Project outcomes
          newValue === 'project_closed_won' || newValue === 'project_closed_lost' || newValue === 'project_cancelled'
        );
      }
      // Direct approval events (changeType is the approval type itself)
      if (item.changeType === '2d_approved' || item.changeType === '3d_approved') return true;
      return false;
    })
    .map(item => {
      const changeType = item.changeType;
      const newValue = (item.newValue || '').toLowerCase();
      const userName = item.user?.nickname || item.user?.fullName || 'Unknown';

      // Determine event type and extract notes
      let eventType = 'unknown';
      let designType = '2D';
      let notes = '';
      let isResubmission = false;

      // Measurement submission/completion (both direct changeType and status_changed with newValue)
      if (changeType === 'measurement_completed' || newValue === 'measurement_done') {
        eventType = 'measurement_submission';
        designType = 'Measurement';
        notes = item.metadata?.completionNotes || item.metadata?.reason || '';
        // Clean up default message if present
        if (notes === 'Complete Measurement') {
          notes = '';
        }
      }
      // 2D/3D Submission (both direct changeType and status_changed with newValue)
      else if (changeType === '2d_submitted' || changeType === '3d_submitted' ||
               newValue === '2d_pending_client_review' || newValue === '3d_pending_client_review') {
        eventType = 'submission';
        // Determine design type from changeType or newValue
        if (changeType === '2d_submitted' || newValue === '2d_pending_client_review') {
          designType = '2D';
        } else {
          designType = '3D';
        }
        notes = item.metadata?.notes || item.metadata?.reason || '';
        isResubmission = item.metadata?.isResubmission || false;
        // Try description if no metadata notes
        if (!notes && item.description) {
          const colonIndex = item.description.indexOf(': ');
          if (colonIndex !== -1) {
            notes = item.description.substring(colonIndex + 2);
          }
        }
        // Clean up default messages if present
        if (notes === 'Submit 2D for Review' || notes === 'Submit 3D for Review') {
          notes = '';
        }
      }
      // DD Submission (both direct changeType and status_changed with newValue)
      else if (changeType === 'dd_submitted' || newValue === 'dd_submitted_to_quotation') {
        eventType = 'dd_submission';
        designType = 'DD';
        notes = item.metadata?.notes || item.metadata?.reason || '';
        // Try description if no metadata notes
        if (!notes && item.description) {
          const colonIndex = item.description.indexOf(': ');
          if (colonIndex !== -1) {
            notes = item.description.substring(colonIndex + 2);
          }
        }
        // Clean up "Submit DD to Quotation" default message if present
        if (notes === 'Submit DD to Quotation') {
          notes = '';
        }
      }
      // Amendment
      else if (newValue === '2d_amendment_required' || newValue === '3d_amendment_required' || newValue === 'signing_amendment_required') {
        eventType = 'amendment';
        designType = newValue === '2d_amendment_required' ? '2D' :
                     newValue === '3d_amendment_required' ? '3D' : 'Signing';
        notes = item.metadata?.notes || item.metadata?.reason || '';
      }
      // Approval (handle both direct changeType and status_changed with newValue)
      else if (changeType === '2d_approved' || changeType === '3d_approved' ||
               newValue === '2d_approved' || newValue === '3d_approved') {
        eventType = 'approval';
        designType = (changeType === '3d_approved' || newValue === '3d_approved') ? '3D' : '2D';
        // Try multiple sources for notes
        notes = item.metadata?.reason || item.metadata?.notes || '';
        // Also try extracting from description if metadata doesn't have the notes
        if (!notes && item.description) {
          // Description format: "Workflow status changed from X to Y: reason"
          const colonIndex = item.description.lastIndexOf(': ');
          if (colonIndex !== -1 && colonIndex > item.description.indexOf('to ')) {
            notes = item.description.substring(colonIndex + 2);
          }
        }
        // Remove default prefix if present to get actual user notes
        if (notes.startsWith('2D design approved: ') || notes.startsWith('3D design approved: ')) {
          notes = notes.replace(/^[23]D design approved: /, '');
        } else if (notes === '2D design approved' || notes === '3D design approved') {
          notes = ''; // Default message, no actual notes
        }
      }
      // Quotation Finalized (direct changeType)
      else if (changeType === 'quotation_finalized') {
        eventType = 'quotation_finalized';
        designType = 'Quotation';
        notes = item.metadata?.notes || '';
        // Add version/total info if available
        if (item.metadata?.quotationVersion) {
          const versionInfo = `Version ${item.metadata.quotationVersion} - RM ${item.metadata.quotationTotal}`;
          notes = notes ? `${versionInfo}. ${notes}` : versionInfo;
        }
      }
      // Quotation Finalized (status_changed fallback)
      else if (newValue === 'ready_for_signing') {
        eventType = 'quotation_finalized';
        designType = 'Quotation';
        notes = item.metadata?.notes || item.metadata?.reason || '';
        // Extract version and total from description if available
        if (!notes && item.description) {
          const match = item.description.match(/Quotation v(\d+) finalized - Total: RM ([\d,]+\.?\d*)/);
          if (match) {
            notes = `Version ${match[1]} - RM ${match[2]}`;
          }
        }
      }
      // Signing Scheduled (direct changeType)
      else if (changeType === 'signing_scheduled') {
        eventType = 'signing_scheduled';
        designType = 'Signing';
        if (item.metadata?.scheduledDate) {
          const schedDate = new Date(item.metadata.scheduledDate);
          const location = item.metadata.location ? ` at ${item.metadata.location}` : '';
          notes = `Scheduled for ${schedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}${location}`;
          if (item.metadata.notes) {
            notes += `. ${item.metadata.notes}`;
          }
        } else {
          notes = item.metadata?.reason || item.metadata?.notes || '';
        }
      }
      // Negotiation Blocker (direct changeType)
      else if (changeType === 'negotiation_blocker') {
        eventType = 'negotiation_blocker';
        designType = 'Signing';
        const blockerType = (item.metadata?.blockerType || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        notes = blockerType ? `${blockerType}: ${item.metadata.description || ''}` : item.metadata?.description || '';
        if (item.metadata?.resolutionPlan) {
          notes += `\nResolution: ${item.metadata.resolutionPlan}`;
        }
        if (item.metadata?.notes) {
          notes += `\n${item.metadata.notes}`;
        }
      }
      // Legacy: Signing Scheduled (changeType 'other' with scheduledDate)
      else if (changeType === 'other' && item.metadata?.scheduledDate) {
        eventType = 'signing_scheduled';
        designType = 'Signing';
        const schedDate = new Date(item.metadata.scheduledDate);
        const location = item.metadata.location ? ` at ${item.metadata.location}` : '';
        notes = `Scheduled for ${schedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}${location}`;
      }
      // Legacy: Negotiation Blocker (changeType 'other' with blockerType)
      else if (changeType === 'other' && item.metadata?.blockerType) {
        eventType = 'negotiation_blocker';
        designType = 'Signing';
        const blockerType = item.metadata.blockerType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        notes = `${blockerType}: ${item.metadata.description || ''}`;
        if (item.metadata.resolutionPlan) {
          notes += `\nResolution: ${item.metadata.resolutionPlan}`;
        }
      }
      // Contract Signed (Won)
      else if (changeType === 'contract_signed' || newValue === 'project_closed_won') {
        eventType = 'project_won';
        designType = 'Contract';
        notes = item.metadata?.notes || item.metadata?.reason || '';
        if (item.metadata?.signedBy) {
          notes = notes ? `Signed by ${item.metadata.signedBy}. ${notes}` : `Signed by ${item.metadata.signedBy}`;
        }
        // Extract from description if no notes
        if (!notes && item.description) {
          const colonIndex = item.description.lastIndexOf(': ');
          if (colonIndex !== -1) {
            notes = item.description.substring(colonIndex + 2);
          }
        }
      }
      // Project Lost
      else if (changeType === 'project_closed' || newValue === 'project_closed_lost') {
        eventType = 'project_lost';
        designType = 'Contract';
        notes = item.metadata?.reason || '';
        // Extract from description if no notes
        if (!notes && item.description) {
          notes = item.description;
        }
      }
      // Project Cancelled
      else if (newValue === 'project_cancelled') {
        eventType = 'project_cancelled';
        designType = 'Project';
        notes = item.metadata?.reason || '';
        if (!notes && item.description) {
          const colonIndex = item.description.lastIndexOf(': ');
          if (colonIndex !== -1) {
            notes = item.description.substring(colonIndex + 2);
          }
        }
      }

      return {
        id: item.id,
        timestamp: item.timestamp || item.createdAt,
        eventType,
        designType,
        notes,
        userName,
        isResubmission
      };
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Most recent first

  // Filter steps by user role
  const relevantSteps = nextSteps.filter(step =>
    step.role.includes('all') || step.role.includes(userRole?.toLowerCase())
  );

  // Priority styling
  const getPriorityStyles = (priority) => {
    const styles = {
      high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: 'text-red-500' },
      medium: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800', icon: 'text-yellow-500' },
      low: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', icon: 'text-gray-400' },
      info: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: 'text-green-500' }
    };
    return styles[priority] || styles.low;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      {/* Header with Status and Progress */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Progress</h3>
          <StatusBadge status={project.workflowStatus} size="sm" />
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm font-medium text-gray-600">{progressPercent}%</span>
          {/* Mobile collapse toggle */}
          <button
            onClick={() => setIsMobileCollapsed(!isMobileCollapsed)}
            className="sm:hidden p-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
            aria-label={isMobileCollapsed ? 'Expand details' : 'Collapse details'}
          >
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${!isMobileCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Progress Bar - Always visible */}
      <div className="mb-3 sm:mb-4">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Phase {currentPhaseIndex + 1}/{workflowPhases.length}: <span className="font-medium">{currentPhaseName}</span>
        </p>
      </div>

      {/* Mobile summary when collapsed */}
      {isMobile && isMobileCollapsed && (
        <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-3">
          <div className="flex items-center gap-3">
            {relevantSteps.length > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-orange-400 rounded-full"></span>
                {relevantSteps.length} action{relevantSteps.length > 1 ? 's' : ''} pending
              </span>
            )}
            {workflowNotes.length > 0 && (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                {workflowNotes.length} note{workflowNotes.length > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <span className="text-blue-600 font-medium">Tap to expand</span>
        </div>
      )}

      {/* Collapsible content (hidden on mobile when collapsed) */}
      <div className={`${isMobile && isMobileCollapsed ? 'hidden' : 'block'}`}>

      {/* Deadline Indicator */}
      {project.currentDeadline && (
        <div className="mb-4">
          {(() => {
            const deadline = formatDeadline(project.currentDeadline);
            const urgencyStyles = {
              overdue: 'bg-red-100 border-red-300 text-red-800',
              critical: 'bg-red-100 border-red-300 text-red-800',
              high: 'bg-orange-100 border-orange-300 text-orange-800',
              medium: 'bg-yellow-100 border-yellow-300 text-yellow-800',
              low: 'bg-green-100 border-green-300 text-green-800',
              none: 'bg-gray-100 border-gray-300 text-gray-800'
            };
            const urgencyIcons = {
              overdue: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ),
              critical: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              high: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              medium: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
              low: (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )
            };
            return (
              <div className={`flex items-center gap-2 p-2.5 rounded-lg border ${urgencyStyles[deadline.urgency]}`}>
                {urgencyIcons[deadline.urgency] || urgencyIcons.low}
                <div className="flex-1">
                  <span className="text-sm font-medium">Deadline: </span>
                  <span className="text-sm">{deadline.text}</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Workflow Notes Section - Combined submissions, amendments, and approvals */}
      {workflowNotes.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Workflow Notes
            <span className="text-xs text-gray-500 font-normal">({workflowNotes.length} entries)</span>
          </h4>
          <div className="space-y-2">
            {(showAllAmendments ? workflowNotes : workflowNotes.slice(0, 3)).map((note, index) => {
              // Determine styling based on event type
              const getEventStyles = () => {
                switch (note.eventType) {
                  case 'amendment':
                    return {
                      bg: index === 0 && isInAmendmentStatus ? 'bg-orange-50' : 'bg-gray-50',
                      border: index === 0 && isInAmendmentStatus ? 'border-orange-300' : 'border-gray-200',
                      text: index === 0 && isInAmendmentStatus ? 'text-orange-900' : 'text-gray-700',
                      label: 'Amendment',
                      labelBg: 'bg-orange-100 text-orange-700'
                    };
                  case 'approval':
                    return {
                      bg: 'bg-green-50',
                      border: 'border-green-200',
                      text: 'text-green-900',
                      label: 'Approved',
                      labelBg: 'bg-green-100 text-green-700'
                    };
                  case 'measurement_submission':
                    return {
                      bg: 'bg-teal-50',
                      border: 'border-teal-200',
                      text: 'text-teal-900',
                      label: 'Submitted',
                      labelBg: 'bg-teal-100 text-teal-700'
                    };
                  case 'dd_submission':
                    return {
                      bg: 'bg-indigo-50',
                      border: 'border-indigo-200',
                      text: 'text-indigo-900',
                      label: 'To Quotation',
                      labelBg: 'bg-indigo-100 text-indigo-700'
                    };
                  case 'quotation_finalized':
                    return {
                      bg: 'bg-purple-50',
                      border: 'border-purple-200',
                      text: 'text-purple-900',
                      label: 'Finalized',
                      labelBg: 'bg-purple-100 text-purple-700'
                    };
                  case 'signing_scheduled':
                    return {
                      bg: 'bg-cyan-50',
                      border: 'border-cyan-200',
                      text: 'text-cyan-900',
                      label: 'Scheduled',
                      labelBg: 'bg-cyan-100 text-cyan-700'
                    };
                  case 'negotiation_blocker':
                    return {
                      bg: 'bg-amber-50',
                      border: 'border-amber-300',
                      text: 'text-amber-900',
                      label: 'Blocker',
                      labelBg: 'bg-amber-100 text-amber-700'
                    };
                  case 'project_won':
                    return {
                      bg: 'bg-emerald-50',
                      border: 'border-emerald-300',
                      text: 'text-emerald-900',
                      label: 'Won',
                      labelBg: 'bg-emerald-100 text-emerald-700'
                    };
                  case 'project_lost':
                    return {
                      bg: 'bg-red-50',
                      border: 'border-red-200',
                      text: 'text-red-900',
                      label: 'Lost',
                      labelBg: 'bg-red-100 text-red-700'
                    };
                  case 'project_cancelled':
                    return {
                      bg: 'bg-gray-100',
                      border: 'border-gray-300',
                      text: 'text-gray-700',
                      label: 'Cancelled',
                      labelBg: 'bg-gray-200 text-gray-600'
                    };
                  case 'submission':
                  default:
                    return {
                      bg: 'bg-blue-50',
                      border: 'border-blue-200',
                      text: 'text-blue-900',
                      label: note.isResubmission ? 'Resubmission' : 'Submission',
                      labelBg: note.isResubmission ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                    };
                }
              };

              const styles = getEventStyles();
              const getActionLabel = () => {
                switch (note.eventType) {
                  case 'amendment': return 'Requested by';
                  case 'approval': return 'Approved by';
                  case 'quotation_finalized': return 'Finalized by';
                  case 'signing_scheduled': return 'Scheduled by';
                  case 'negotiation_blocker': return 'Recorded by';
                  case 'project_won': return 'Closed by';
                  case 'project_lost': return 'Recorded by';
                  case 'project_cancelled': return 'Cancelled by';
                  default: return 'Submitted by';
                }
              };
              const actionLabel = getActionLabel();

              return (
                <div
                  key={note.id || index}
                  className={`p-3 rounded-lg border ${styles.bg} ${styles.border}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        note.designType === '2D' ? 'bg-blue-100 text-blue-700' :
                        note.designType === '3D' ? 'bg-purple-100 text-purple-700' :
                        note.designType === 'DD' ? 'bg-indigo-100 text-indigo-700' :
                        note.designType === 'Quotation' ? 'bg-violet-100 text-violet-700' :
                        note.designType === 'Signing' ? 'bg-cyan-100 text-cyan-700' :
                        note.designType === 'Contract' ? 'bg-emerald-100 text-emerald-700' :
                        note.designType === 'Project' ? 'bg-gray-100 text-gray-700' :
                        'bg-teal-100 text-teal-700'
                      }`}>
                        {note.designType}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles.labelBg}`}>
                        {styles.label}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(note.timestamp).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {note.notes ? (
                    <p className={`text-sm whitespace-pre-wrap ${styles.text}`}>
                      {note.notes}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      {note.eventType === 'approval' ? 'Approved without notes' : 'No notes provided'}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    {actionLabel}: {note.userName}
                  </p>
                </div>
              );
            })}

            {workflowNotes.length > 3 && (
              <button
                onClick={() => setShowAllAmendments(!showAllAmendments)}
                className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
              >
                {showAllAmendments ? (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                    Show less
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    View {workflowNotes.length - 3} more entries
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {relevantSteps.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Next Steps
          </h4>
          <div className="space-y-2">
            {relevantSteps.slice(0, showFullProgress ? undefined : 2).map((step, index) => {
              const styles = getPriorityStyles(step.priority);
              return (
                <div
                  key={index}
                  className={`flex items-start gap-2 p-2.5 rounded-lg border ${styles.bg} ${styles.border}`}
                >
                  {step.priority === 'high' ? (
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  ) : step.priority === 'info' ? (
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <span className={`text-sm ${styles.text}`}>{step.text}</span>
                </div>
              );
            })}
          </div>
          {relevantSteps.length > 2 && !showFullProgress && (
            <button
              onClick={() => setShowFullProgress(true)}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              +{relevantSteps.length - 2} more steps
            </button>
          )}
        </div>
      )}

      {/* No steps for role */}
      {relevantSteps.length === 0 && (
        <div className="text-center py-4">
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-500">No action items for your role</p>
        </div>
      )}
      </div>
      {/* End of collapsible content wrapper */}

      {/* Expandable Full Progress View - always visible */}
      <button
        onClick={() => setShowFullProgress(!showFullProgress)}
        className="w-full mt-2 pt-3 border-t border-gray-100 flex items-center justify-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        {showFullProgress ? 'Hide Details' : 'View Full Progress'}
        <svg
          className={`w-4 h-4 transition-transform duration-200 ${showFullProgress ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Full Progress Phases */}
      {showFullProgress && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="relative">
            {workflowPhases.map((phase, index) => {
              const isCompleted = index < currentPhaseIndex;
              const isCurrent = index === currentPhaseIndex;

              return (
                <div key={phase.name} className="flex items-start mb-3 last:mb-0">
                  {/* Circle */}
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${
                      isCompleted
                        ? 'bg-green-500 border-green-500 text-white'
                        : isCurrent
                        ? 'bg-blue-500 border-blue-500 text-white animate-pulse'
                        : 'bg-white border-gray-300 text-gray-400'
                    }`}
                  >
                    {isCompleted ? (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-xs font-bold">{index + 1}</span>
                    )}
                  </div>

                  {/* Phase Name */}
                  <span
                    className={`ml-3 text-sm ${
                      isCurrent
                        ? 'font-semibold text-blue-600'
                        : isCompleted
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }`}
                  >
                    {phase.name}
                    {isCurrent && <span className="ml-2 text-xs font-normal">(Current)</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectProgressPanel;
